import { customElement, resolve } from 'aurelia';
import { route } from '@aurelia/router';
import { I18N } from '@aurelia/i18n';
import template from './follow-ups-view.html';
import type { Document, FollowUpItem } from '../services/api.types';
import { getDocument, getMe, listUpcomingFollowUps, removeFollowUp, createImportProtocol, completeImportProtocol } from '../services/api.service';
import { ConfirmDialog } from '../components/confirm-dialog';
import { formatDate as formatDateService } from '../services/date-format.service';

@route({ path: 'followups', title: 'Follow-ups' })
@customElement({ name: 'follow-ups-view', template })
export class FollowUpsView {
  private readonly i18n: I18N = resolve(I18N);
  public items: FollowUpItem[] = [];
  public isFetching = false;
  public fetchError = '';
  public viewDocument: Document | null = null;
  public editDocument: Document | null = null;
  public initialFilesForEditModal: File[] = [];
  public pendingImportProtocolNumber: number | null = null;
  public currentUsername = '';

  public get groupedItems(): { key: FollowUpItem['group']; titleKey: string; titleClass: string; items: FollowUpItem[] }[] {
    return [
      { key: 'overdue', titleKey: 'followUps.groupOverdue', titleClass: 'followup-group__title--overdue', items: this.items.filter(i => i.group === 'overdue') },
      { key: 'today', titleKey: 'followUps.groupToday', titleClass: 'followup-group__title--today', items: this.items.filter(i => i.group === 'today') },
      { key: 'tomorrow', titleKey: 'followUps.groupTomorrow', titleClass: 'followup-group__title--tomorrow', items: this.items.filter(i => i.group === 'tomorrow') },
      { key: 'upcoming', titleKey: 'followUps.groupUpcoming', titleClass: 'followup-group__title--upcoming', items: this.items.filter(i => i.group === 'upcoming') },
    ];
  }

  public get hasItems(): boolean {
    return this.items.length > 0;
  }

  public async loading(): Promise<void> {
    const me = await getMe().catch(() => null);
    this.currentUsername = me?.username ?? '';
    await this.fetchItems();
  }

  public async fetchItems(): Promise<void> {
    this.isFetching = true;
    this.fetchError = '';
    try {
      this.items = await listUpcomingFollowUps(7);
    } catch (err: unknown) {
      this.fetchError = err instanceof Error ? err.message : this.i18n.tr('followUps.load_failed');
    } finally {
      this.isFetching = false;
    }
  }

  public async markDone(item: FollowUpItem): Promise<void> {
    const confirmed = await ConfirmDialog.confirm(this.i18n.tr('followUps.markDone_confirm'), this.i18n.tr('followUps.markDone_confirmTitle'));
    if (!confirmed) return;
    try {
      await removeFollowUp(item.documentId, item.followUpId);
      this.items = this.items.filter(i => i.followUpId !== item.followUpId);
    } catch (err: unknown) {
      await ConfirmDialog.alert(err instanceof Error ? err.message : this.i18n.tr('followUps.delete_failed'), this.i18n.tr('common.error'));
    }
  }

  public async onRowDblClick(item: FollowUpItem, event: MouseEvent): Promise<void> {
    try {
      const doc = await getDocument(item.documentId);
      if (event.ctrlKey) {
        this.editDocument = doc;
        this.viewDocument = null;
      } else {
        this.viewDocument = doc;
        this.editDocument = null;
      }
    } catch { /* ignore */ }
  }

  public onViewClosed(): void {
    this.viewDocument = null;
  }

  public async onViewDocumentUpdated(doc: Document): Promise<void> {
    this.viewDocument = doc;
    await this.fetchItems();
  }

  public async onEditFromView(doc: Document, files?: File[]): Promise<void> {
    if (files?.length) {
      try {
        const protocol = await createImportProtocol();
        if (!Number.isSafeInteger(protocol.number) || protocol.number <= 0) {
          throw new Error(this.i18n.tr('importProtocols.invalidNumber'));
        }
        this.pendingImportProtocolNumber = protocol.number;
      } catch (error: unknown) {
        await ConfirmDialog.alert(
          error instanceof Error ? error.message : this.i18n.tr('documents.dropZone.createError'),
          this.i18n.tr('common.error'),
        );
        return;
      }
      this.initialFilesForEditModal = [...files];
    }
    this.editDocument = doc;
    this.viewDocument = null;
  }

  public async onDocumentSaved(): Promise<void> {
    await this.finishPendingImportProtocol();
    this.editDocument = null;
    this.initialFilesForEditModal = [];
    await this.fetchItems();
  }

  public async onModalClosed(): Promise<void> {
    await this.finishPendingImportProtocol();
    this.editDocument = null;
    this.viewDocument = null;
    this.initialFilesForEditModal = [];
  }

  public async onDocumentDeleted(): Promise<void> {
    await this.finishPendingImportProtocol();
    this.editDocument = null;
    this.viewDocument = null;
    this.initialFilesForEditModal = [];
    await this.fetchItems();
  }

  private async finishPendingImportProtocol(): Promise<void> {
    const number = this.pendingImportProtocolNumber;
    this.pendingImportProtocolNumber = null;
    if (number === null) return;
    try {
      await completeImportProtocol(number);
    } catch (error) {
      console.error('[Radish DMS] Could not complete import protocol', {
        protocolNumber: number,
        error,
      });
    }
  }

  public formatDate(iso: string): string {
    return formatDateService(iso);
  }

  public displayName(username?: string | null, name?: string | null): string {
    return name || username || '';
  }

  public followUpForName(item: FollowUpItem): string {
    return this.displayName(item.forUser, item.forUserDisplayName);
  }

  public followUpCreatorName(item: FollowUpItem): string {
    return this.displayName(undefined, item.creatorDisplayName);
  }

  public showForUser(item: FollowUpItem): boolean {
    return !!item.forUser && !this.isCurrentUser(item.forUser);
  }

  public showCreator(item: FollowUpItem): boolean {
    return !!item.creatorDisplayName && item.creatorDisplayName.toLowerCase() !== this.followUpForName(item).toLowerCase();
  }

  public dateBadgeClass(group: FollowUpItem['group']): string {
    return `fu-card__date-badge fu-card__date-badge--${group}`;
  }

  private isCurrentUser(username?: string | null): boolean {
    return !!username && !!this.currentUsername && username.toLowerCase() === this.currentUsername.toLowerCase();
  }
}
